import { Tag as AntTag } from 'antd';
import { StarFilled } from '@ant-design/icons';
import { brandColors } from '@viq-admin/theme';
import { space } from '@viq-admin/ui';
import type { BatchTagsApi, CustomTagDefinition, LeadTag } from './types';
import { TagPicker } from './TagPicker';

const STATUS_COLOR: Record<string, string> = {
  lead: brandColors.pillLead,
  contact: brandColors.pillContact,
  customer: brandColors.pillCustomer,
  ex_customer: brandColors.pillExCustomer,
  partner: brandColors.pillPartner,
  competitor: brandColors.pillCompetitor,
  unknown: brandColors.pillLead,
};

interface Props {
  personId: string;
  tags: LeadTag[];
  customTagDefinitions: CustomTagDefinition[];
  tagsApi: BatchTagsApi;
  onTagsChange: (tags: LeadTag[]) => void;
  /**
   * Read-only — renders the pills without the ⊕ TagPicker (no add/remove). Used
   * by the portal's stakeholder single-pane. Default (undefined/false) keeps the
   * editable picker, so the console is unchanged.
   */
  readOnly?: boolean;
}

/**
 * EDITABLE tags cell (Batch-2). Renders VIP star + status/custom pills, then a
 * ⊕ TagPicker for adding/removing tags. Ported from the portal's
 * `src/views/BatchScreen/TagsCell.tsx`; the mutation endpoints arrive via the
 * shell-provided `tagsApi` instead of a direct module import.
 */
export function TagsCell({
  personId,
  tags,
  customTagDefinitions,
  tagsApi,
  onTagsChange,
  readOnly,
}: Props) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.xs, alignItems: 'center' }}>
      {tags.map((t) => {
        if (t.category === 'vip') {
          return (
            <StarFilled
              key="vip"
              style={{ color: brandColors.signatureGold, fontSize: 16 }}
              aria-label="VIP"
            />
          );
        }
        if (t.category === 'status') {
          const color = STATUS_COLOR[t.value] ?? brandColors.pillLead;
          return (
            <AntTag key={`status-${t.value}`} color={color} style={{ borderRadius: 12, margin: 0 }}>
              {t.value}
            </AntTag>
          );
        }
        return (
          <AntTag
            key={`custom-${t.value}`}
            color={brandColors.pillPartner}
            style={{ borderRadius: 12, margin: 0 }}
          >
            {t.value}
          </AntTag>
        );
      })}
      {!readOnly && (
        <TagPicker
          personId={personId}
          currentTags={tags}
          customTagDefinitions={customTagDefinitions}
          tagsApi={tagsApi}
          onTagsChange={onTagsChange}
        />
      )}
    </div>
  );
}
